/** Merge a single batch card patch into a cached production calendar response. */

export type ProductionCalendarBatchPatch = {
  batchId: number;
  mfrShipByDate: string | null;
  stationCounts: Record<string, number>;
  stationCountsDisplay?: Record<string, number>;
  stationSkuBreakdown: {
    stationId: number | null;
    sku: string;
    count: number;
    divisor?: number;
    clampUnits?: number;
  }[];
  unassignedCount: number;
  trackedTotal: number;
  completedCount: number;
  unserializedQcOpenQty: number;
  unserializedQcOpenClamp: number;
  unserializedPackOutOpenQty: number;
  unserializedPackOutOpenClamp: number;
  unserializedQcOpenBySku: { sku: string; quantity: number; clampUnits?: number }[];
  unserializedPackOutOpenBySku: { sku: string; quantity: number; clampUnits?: number }[];
  unserializedOnShipmentBySku: { sku: string; quantity: number }[];
  shippedDetailBySkuStation: { sku: string; stationName: string; count: number }[];
  shipments?: unknown[];
  shipmentOnHold?: boolean;
  [key: string]: unknown;
};

type BatchListRow = { batchId: number };

function mergeBatchInList<T extends BatchListRow>(list: T[], batch: T): { list: T[]; merged: boolean } {
  const index = list.findIndex((row) => row.batchId === batch.batchId);
  if (index < 0) {
    return { list, merged: false };
  }
  const next = list.slice();
  next[index] = { ...next[index], ...batch };
  return { list: next, merged: true };
}

export function mergeProductionCalendarBatchPatch<
  T extends { batches: B[]; overdueBatches: B[]; searchResults?: B[] },
  B extends BatchListRow,
>(response: T, batch: B): T {
  const inWindow = mergeBatchInList(response.batches, batch);
  const overdue = mergeBatchInList(response.overdueBatches, batch);
  const search = mergeBatchInList(response.searchResults ?? [], batch);
  if (!inWindow.merged && !overdue.merged && !search.merged) {
    return response;
  }
  return {
    ...response,
    batches: inWindow.list,
    overdueBatches: overdue.list,
    ...(response.searchResults != null ? { searchResults: search.list } : {}),
  };
}
